import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormWrapper } from '../../components';
import { track, actions } from 'react-redux-form';
import { setChart, setStats } from '../../reducers/results';
import moment from 'moment';
import _ from 'lodash';
import { BT_START, TIMEZONE } from '../../config/constants';
import { round10 } from '../../config/utils';

import { getHistoricalData } from '../../data/provider';

const handleSubmit = async (values, dispatch) => {
  let { stockAllocation, bitcoinPercentage, withRebalancing } = values;

  let startingAmount = Number(values.startingAmount.replace(/[^\d.]/g,''));
  let startingDate = values.startingDate;
  let ratioCoin = bitcoinPercentage/100; // BTC
  let bondAllocation = 100 - stockAllocation;
  let ratioStock = (stockAllocation - (bitcoinPercentage * stockAllocation / 100)) / 100; // VT
  let ratioBond = (bondAllocation - (bitcoinPercentage * bondAllocation / 100)) / 100; // BND
  // console.log(`Stock ${ratioStock * 100}% / Bond ${ratioBond * 100}% / Bitcoin ${ratioCoin * 100}%`);

  let { VT, BND, BTC } = await getHistoricalData();
  // console.log(VT, BND, BTC);
  
  let startStockIndex = _.findIndex(VT, (entry) => {
    return entry.date.isSame(startingDate, 'day');
  });
  let startBondIndex = _.findIndex(BND, (entry) => {
    return entry.date.isSame(startingDate, 'day');
  });
  let startCoinIndex = _.findIndex(BTC, (entry) => {
    return entry.date.isSame(startingDate, 'day');
  });
  // console.log(`Start Index: ${startStockIndex} ${startBondIndex} ${startCoinIndex}`);

  if (startStockIndex < 0 || startBondIndex < 0 || startCoinIndex < 0) {
    console.log(`Could not find entries for ${startingDate.format('MM/DD/YYYY')}`);
    dispatch(actions.change(`input.startingDate`, BT_START));
    alert("Invalid date!");
    return;
  }

  // create normalized ticker data
  let totalEntries = Math.min(
    VT.length - startStockIndex,
    BND.length - startBondIndex,
    BTC.length - startCoinIndex
  );
  let vt = VT.slice(startStockIndex, startStockIndex + totalEntries).map(d => d.close);
  let bnd = BND.slice(startBondIndex, startBondIndex + totalEntries).map(d => d.close);
  let btc = BTC.slice(startCoinIndex, startCoinIndex + totalEntries).map(d => d.close);
  // use any index
  let dates = BND.slice(startBondIndex, startBondIndex + totalEntries).map(d => d.date);

  // console.log(`data sizes (should be ${totalEntries})`, dates.length, vt.length, bnd.length, btc.length);
  
  let ticker = _.zipWith(dates, vt, bnd, btc, (date, stock, bond, coin) => {
    // console.log(date, stock, bond, coin);
    return {
      name: typeof date !== "undefined" ? date.tz(TIMEZONE).format('YYYY-MM-DD') : "",
      date, 
      stock, 
      bond, 
      coin
    };
  });

  // prep to calculate portfolio based on pre-prepped user data
  let stockAmountTraditional = startingAmount * stockAllocation / 100 / ticker[0].stock;
  let stockAmountWithCoin = startingAmount * ratioStock / ticker[0].stock;
  let bondAmountTraditional = startingAmount * bondAllocation / 100 / ticker[0].bond;
  let bondAmountWithCoin = startingAmount * ratioBond / ticker[0].bond;
  let amountCoin = startingAmount * ratioCoin / ticker[0].coin;

  let tolerance = ratioCoin * 0.5;
  let toleranceLow = Math.max(ratioCoin - tolerance, 0);
  let toleranceHigh = Math.min(ratioCoin + tolerance, 1);

  // calculate portfolio amounts based on ticker data
  let results = _.map(ticker, (data, index) => {
    let { name, date, stock, bond, coin } = data;

    let stockTotalTraditional = stockAmountTraditional * stock;
    let bondTotalTraditional = bondAmountTraditional * bond;
    let traditional = stockTotalTraditional + bondTotalTraditional;

    let stockTotalEnhanced = stockAmountWithCoin * stock;
    let bondTotalEnhanced = bondAmountWithCoin * bond;
    let coinTotal = amountCoin * coin;
    let enhanced = stockTotalEnhanced + bondTotalEnhanced + coinTotal;

    /*
      50% tolerance based rebalancing
      At any time if the Bitcoin allocation exceeds 50% of the target allocation it is rebalanced 
      i.e. if target is 5% it exceeds 2.5% or 7.5% Bitcoin is sold at that day’s spot price and 
      buy stocks/bonds to return to target allocation.
    */
    if (withRebalancing) {
      let actualCoinAllocation = coinTotal / enhanced;
      if (actualCoinAllocation < toleranceLow || actualCoinAllocation > toleranceHigh) {
        // rebalance, i.e.,
        // low: buy coin and sell stocks/bonds to meet target allocation
        // high: sell coin and buy stocks/bonds to meet target allocation
        // either way, we can simply re-allocate using today's close

        // console.log(`Rebalancing for ${actualCoinAllocation * 100}% coin allocation on ${date} (#${index}).`);
        // console.log(`Low (${toleranceLow * 100}%) & High (${toleranceHigh * 100}%)`);
        // console.log(`Old balance:\n stock ${stockAmountWithCoin} / bond ${bondAmountWithCoin} / coin ${amountCoin}`);

        stockAmountWithCoin = enhanced * ratioStock / stock;
        bondAmountWithCoin = enhanced * ratioBond / bond;
        amountCoin = enhanced * ratioCoin / coin;

        // console.log(`Ticker for ${name}: stock ${stock} / bond ${bond} / coin ${coin}`);
        // console.log(`New balance:\n stock ${stockAmountWithCoin} / bond ${bondAmountWithCoin} / coin ${amountCoin}`);
        // console.log(`Old coin ${coinTotal}; new coin ${amountCoin * coin}`);

        stockTotalEnhanced = stockAmountWithCoin * stock;
        bondTotalEnhanced = bondAmountWithCoin * bond;
        coinTotal = amountCoin * coin;
        enhanced = stockTotalEnhanced + bondTotalEnhanced + coinTotal;
      }
    }

    return {
      name, date, traditional, enhanced, 
      stockTotalTraditional,
      bondTotalTraditional,
      stockTotalEnhanced,
      bondTotalEnhanced,
      coinTotal,
    };
  });

  // quick fix to remove the last day when timezones don't match up
  const REMOVE_THRESHOLD = 10;
  let removedResults = 0;
  let lastResult = results[results.length - 1];
  console.log(lastResult);
  while (isNaN(lastResult.enhanced) || isNaN(lastResult.traditional)) {
    if (removedResults >= REMOVE_THRESHOLD) {
      alert("Something went really wrong calculating the results! Please try again later.");
      return;
    }
    removedResults++;
    lastResult = results[results.length - removedResults - 1];
    console.log(lastResult);
  }
  if (removedResults > 0) {
    results = _.slice(results, 0, results.length - removedResults);
  }
  console.log("trimmed " + removedResults + " result(s)");

  // console.log("data");
  // console.log(_.map(results, (data) => {
  //   let { name, traditional, enhanced } = data;
  //   return `${name},${traditional},${enhanced}`;
  // }).join("\n"));

  // chart data shows ROI %
  let chart = _.map(results, (data, index) => {
    let { name, date, traditional, enhanced } = data;

    // calculate ROI
    traditional = (traditional - startingAmount) / startingAmount * 100;
    enhanced = (enhanced - startingAmount) / startingAmount * 100;

    return { name, date, traditional, enhanced };
  });
  dispatch(setChart(chart));

  // create daily returns (size is returns - 1)
  // first data point is ln(returns[1] / returns[0])
  // each data point thereafter is returns[n] / returns[n-1] - 1
  let dailyReturns = _.map(results.slice(1), (data, index, collection) => {
    let { name, date, traditional, enhanced } = data;

    if (index === 0) {
      traditional = Math.log(traditional / results[index].traditional);
      enhanced = Math.log(enhanced / results[index].enhanced);
    } else {
      traditional = traditional / results[index].traditional - 1;
      enhanced = enhanced / results[index].enhanced - 1;
    }

    return {
      name,
      date,
      traditional,
      enhanced,
    };
  });

  // console.log("daily returns");
  // console.log(_.map(dailyReturns, (data) => {
  //   let { name, traditional, enhanced } = data;
  //   return `${name},${traditional},${enhanced}`;
  // }).join("\n"));

  let totalDays = results.length;
  const DAYS_PER_YEAR = 252; // weekdays
  let workingYears = totalDays / DAYS_PER_YEAR;

  console.log(chart);

  // calculate total returns
  let lastIndex = chart.length - 1;
  let totalReturnPercentageTraditional = chart[lastIndex].traditional;
  let totalReturnPercentageEnhanced = chart[lastIndex].enhanced;
  let totalReturnDollarsTraditional = results[lastIndex].traditional - results[0].traditional;
  let totalReturnDollarsEnhanced = results[lastIndex].enhanced - results[0].enhanced;

  // calculate annualized returns
  // Annualized Return = (1 + cumulative return) ^ (365 / days held) - 1
  // https://www.investopedia.com/terms/a/annualized-total-return.asp
  // let daysHeld = chart[lastIndex].date.diff(chart[0].date, 'days');
  let fractionOfTotalTime = DAYS_PER_YEAR / totalDays;
  let annualizedReturnTraditional = (1 + totalReturnPercentageTraditional / 100) ** fractionOfTotalTime - 1;
  let annualizedReturnEnhanced = (1 + totalReturnPercentageEnhanced / 100) ** fractionOfTotalTime - 1;
  
  let annualizedReturnPercentageTraditional = annualizedReturnTraditional * 100;
  let annualizedReturnPercentageEnhanced = annualizedReturnEnhanced * 100;
  let annualizedReturnDollarsTraditional = annualizedReturnTraditional * startingAmount;
  let annualizedReturnDollarsEnhanced = annualizedReturnEnhanced * startingAmount;

  // calculate Sharpe Ratio (Risk adjusted return)
  // Sharpe ratio = (Mean portfolio return − Risk-free rate)/Standard deviation of portfolio return
  // Risk-free rate
  /* Jonney Liu: We will set this as constant for now since we've had low rate environment 
    for quite some while and 1% is about average. If we really wanted to be accurate, 
    we would need daily interest rates, or interest rates by year which will make in 
    unnecessarily complicated
    */
  const RISK_FREE_RATE = 0.01;

  // standard deviation
  // https://www.khanacademy.org/math/probability/data-distributions-a1/summarizing-spread-distributions/a/calculating-standard-deviation-step-by-step
  // 1. find the mean of all data points
  // 2. subtract the mean from each data point, and square the results
  // 3. sum all the results and divide by # of data points
  // 4. take the square root of the division

  // use the earlier calculated daily returns as data points
  let sums = _.reduce(dailyReturns, (totals, data) => {
    let { traditional, enhanced } = data;
    let { traditionalTotal, enhancedTotal } = totals;

    return {
      traditionalTotal: traditional + traditionalTotal, 
      enhancedTotal: enhanced + enhancedTotal, 
    };
  }, {
    traditionalTotal: 0, 
    enhancedTotal: 0,
  });
  let n = dailyReturns.length;
  let means = {
    traditional: sums.traditionalTotal / n, 
    enhanced: sums.enhancedTotal / n, 
  };
  console.log("means", means);

  let stdevPrep = _.reduce(dailyReturns, (totals, data) => {
    let { traditional, enhanced } = data;
    let { traditionalTotal, enhancedTotal } = totals;
    
    return {
      traditionalTotal: (traditional - means.traditional) ** 2 + traditionalTotal, 
      enhancedTotal: (enhanced - means.enhanced) ** 2 + enhancedTotal, 
    };
  }, {traditionalTotal: 0, enhancedTotal: 0});
  let stdev = {
    traditional: Math.sqrt(stdevPrep.traditionalTotal / n),
    enhanced: Math.sqrt(stdevPrep.enhancedTotal / n),
  };
  console.log("stdev", stdev);

  // annualized volatility
  // STDEV(all daily returns %) * sqrt(days/year)
  let anualizedVolatilityTraditional = stdev.traditional * Math.sqrt(DAYS_PER_YEAR);
  let anualizedVolatilityEnhanced = stdev.enhanced * Math.sqrt(DAYS_PER_YEAR);

  console.log("annualized volatility", anualizedVolatilityTraditional, anualizedVolatilityEnhanced);

  // (annualized return % - risk free rate) / annualized volatility
  let sharpeTraditional = (annualizedReturnTraditional - RISK_FREE_RATE) / anualizedVolatilityTraditional;
  let sharpeEnhanced = (annualizedReturnEnhanced - RISK_FREE_RATE) / anualizedVolatilityEnhanced;

  // calculate Max Drawdown
  // https://www.investopedia.com/terms/m/maximum-drawdown-mdd.asp
  let drawdownPrepTraditional = _.reduce(results, (stats, data, index) => {
    let { previousHigh, previousLow, high, low } = stats;
    let { traditional } = data;
    if (traditional > high) {
      if (high - low > previousHigh - previousLow) {
        previousHigh = high;
        previousLow = low;
      }
      high = traditional;
      low = traditional;
    } else if (traditional < high) {
      if (traditional < low) {
        low = traditional;
      }
    }

    return {
      previousHigh,
      previousLow,
      high,
      low,
    };
  }, {
    previousHigh: 0,
    previousLow: 0,
    high: 0,
    low: 0,
  });
  let traditionalHigh;
  let traditionalLow;
  if (drawdownPrepTraditional.high - drawdownPrepTraditional.low > 
    drawdownPrepTraditional.previousHigh - drawdownPrepTraditional.previousLow
  ) {
    traditionalHigh = drawdownPrepTraditional.high;
    traditionalLow = drawdownPrepTraditional.low;
  } else {
    traditionalHigh = drawdownPrepTraditional.previousHigh;
    traditionalLow = drawdownPrepTraditional.previousLow;
  }
  let maxDrawDownTraditional = (traditionalLow - traditionalHigh) / traditionalHigh * 100;

  let drawdownPrepEnhanced = _.reduce(results, (stats, data, index) => {
    let { previousHigh, previousLow, high, low } = stats;
    let { enhanced } = data;
    if (enhanced > high) {
      if (high - low > previousHigh - previousLow) {
        previousHigh = high;
        previousLow = low;
      }
      high = enhanced;
      low = enhanced;
    } else if (enhanced < high) {
      if (enhanced < low) {
        low = enhanced;
      }
    }

    return {
      previousHigh,
      previousLow,
      high,
      low,
    };
  }, {
    previousHigh: 0,
    previousLow: 0,
    high: 0,
    low: 0,
  });
  let enhancedHigh;
  let enhancedLow;
  if (drawdownPrepEnhanced.high - drawdownPrepEnhanced.low > 
    drawdownPrepEnhanced.previousHigh - drawdownPrepEnhanced.previousLow
  ) {
    enhancedHigh = drawdownPrepEnhanced.high;
    enhancedLow = drawdownPrepEnhanced.low;
  } else {
    enhancedHigh = drawdownPrepEnhanced.previousHigh;
    enhancedLow = drawdownPrepEnhanced.previousLow;
  }
  let maxDrawDownEnhanced = (enhancedLow - enhancedHigh) / enhancedHigh * 100;

  console.log(
    totalReturnPercentageTraditional, 
    totalReturnPercentageEnhanced, 
    totalReturnDollarsTraditional, 
    totalReturnDollarsEnhanced,
    annualizedReturnPercentageTraditional,
    annualizedReturnPercentageEnhanced,
    annualizedReturnDollarsTraditional,
    annualizedReturnDollarsEnhanced,
    sharpeTraditional,
    sharpeEnhanced,
    maxDrawDownTraditional, 
    maxDrawDownEnhanced
  );
  dispatch(setStats({
    bitcoinPercentage,
    withRebalancing,
    totalReturnPercentageTraditional, 
    totalReturnPercentageEnhanced, 
    totalReturnDollarsTraditional, 
    totalReturnDollarsEnhanced,
    annualizedReturnPercentageTraditional,
    annualizedReturnPercentageEnhanced,
    annualizedReturnDollarsTraditional,
    annualizedReturnDollarsEnhanced,
    sharpeTraditional,
    sharpeEnhanced,
    maxDrawDownTraditional, 
    maxDrawDownEnhanced,
  }));

  // let startStock = _.find(VT, (entry) => {
  //   return moment(entry.date, 'MM/DD/YYYY').isSame(startingDate, 'day');
  // });
  // let startBond = _.find(BND, (entry) => {
  //   return moment(entry.date, 'MM/DD/YYYY').isSame(startingDate, 'day');
  // });

  // let endStock = _.find(VT, (entry) => {
  //   return moment(entry.date, 'MM/DD/YYYY').isSame(END_DATE, 'day');
  // });
  // let endBond = _.find(BND, (entry) => {
  //   return moment(entry.date, 'MM/DD/YYYY').isSame(END_DATE, 'day');
  // });

  // console.log(`Can buy ${amountStock} @ ${startStock.close} from ${startingAmount * ratioStock}`);
  // console.log(`Can buy ${amountBond} @ ${startBond.close} from ${startingAmount * ratioBond}`);

  // let endPriceStock = endStock.close * amountStock;
  // let endPriceBond = endBond.close * amountBond;
  // let endingAmount = endPriceStock + endPriceBond;

  // let increase = endingAmount / startingAmount * 100;

  // console.log(`${amountStock} shares of VT ending at ${endPriceStock} @ ${endStock.close} / share`);
  // console.log(`${amountBond} shares of BND ending at ${endPriceBond} @ ${endBond.close} / share`);
  // console.log(`${startingAmount} to ${endingAmount} (${increase}%)`);
  // console.log("Form submitted");
}

const mapStateToProps = state => {
  let { bitcoinPercentage, stockAllocation } = state.input;
  let ratioCrypto = bitcoinPercentage; // BTC
  let bondAllocation = 100 - stockAllocation;
  let ratioStock = round10(stockAllocation - (bitcoinPercentage * stockAllocation / 100), -1); // VT
  let ratioBond = round10(bondAllocation - (bitcoinPercentage * bondAllocation / 100), -1); // BND

  return {
    input: state.input,
    minDate: BT_START,
    maxDate: moment.tz(TIMEZONE),
    ratioCrypto,
    ratioStock,
    ratioBond,
    calculated: state.results.stats.hasStats,
    isWeekday: (val) => { //val is moment
      let weekday = val.isoWeekday();
      let isWeekday = weekday < 6; // 1 = Monday, 7 = Sunday

      if (!isWeekday) return false;

      let dateIndex = _.findIndex(state.tickers.dates, (date) => val.isSame(date, 'day'));
      return dateIndex > -1;
    },
  }
}

const mapDispatchToProps = dispatch => {
  return {
    updateControl: (control, value) => {
      dispatch(actions.change(`input.${control}`, value));
    },
    handleSubmit: (values) => handleSubmit(values, dispatch),
  }
}

export const FormWrapperContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(FormWrapper);


// WEBPACK FOOTER //
// ./src/containers/FormWrapper/FormWrapperContainer.js